// Persisted job LOG for the native (Capacitor Android) printing surface:
// metadata only — `data` (rendered bytes) is STRIPPED, so restored jobs are
// visible in the Jobs tab but not reprintable. Lives under its OWN
// localStorage key so persist()'s fixed-point guard on `printing-settings`
// is untouched.
import { IPrintJob } from '@/interfaces/printing.interfaces';
import { PrinterType } from '@/modules/PrintService';

export const JOBS_LOG_STORAGE_KEY = 'printing-jobs-log';
/** Rolling cap of the in-memory list AND the persisted log. */
export const JOBS_LOG_MAX = 50;

/** Serialized shape: Date → ISO string, bytes stripped. */
type TPersistedJob = Omit<IPrintJob, 'createdAt' | 'data'> & {
  createdAt: string;
};

const VALID_STATUSES = new Set(['printed', 'failed', 'unknown']);

/**
 * Reads and revives the persisted log. Malformed payloads (hand-edited,
 * older shapes) degrade to fewer/zero entries — never a throw at store init.
 */
export function readJobsLog(): IPrintJob[] {
  try {
    const raw = localStorage.getItem(JOBS_LOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const jobs: IPrintJob[] = [];
    for (const entry of parsed as Partial<TPersistedJob>[]) {
      if (!entry || typeof entry !== 'object') continue;
      if (typeof entry.jobId !== 'string' || !entry.jobId) continue;
      if (typeof entry.status !== 'string' || !VALID_STATUSES.has(entry.status))
        continue;
      if (typeof entry.title !== 'string') continue;
      const createdAt = new Date(entry.createdAt ?? NaN);
      if (Number.isNaN(createdAt.getTime())) continue;
      const printerType = Number(entry.printerType);
      if (!Number.isInteger(printerType)) continue;
      jobs.push({
        ...entry,
        jobId: entry.jobId,
        title: entry.title,
        status: entry.status,
        printerType: printerType as PrinterType,
        createdAt,
      });
      if (jobs.length >= JOBS_LOG_MAX) break;
    }
    return jobs;
  } catch (e) {
    console.error('[Printing] could not read the persisted jobs log', e);
    return [];
  }
}

/** Persists the log (metadata only, rolling cap). */
export function writeJobsLog(jobs: IPrintJob[]): void {
  try {
    const persisted: TPersistedJob[] = jobs.slice(0, JOBS_LOG_MAX).map((job) => {
      // Strip bytes explicitly (a 50-job log with payloads could blow the
      // localStorage quota on the low-end tablets this targets).
      const { data: _data, ...rest } = job;
      return { ...rest, createdAt: job.createdAt.toISOString() };
    });
    localStorage.setItem(JOBS_LOG_STORAGE_KEY, JSON.stringify(persisted));
  } catch (e) {
    console.error('[Printing] could not persist the jobs log', e);
  }
}

export function clearJobsLog(): void {
  try {
    localStorage.removeItem(JOBS_LOG_STORAGE_KEY);
  } catch (e) {
    console.error('[Printing] could not clear the jobs log', e);
  }
}
